import {
  type DesktopDownloadChannel,
  type DesktopDownloads,
  type DesktopInstallerTarget,
  type DesktopUpdateChannel,
  desktopDownloadsSchema,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';

/*
 * Crowdex Desktop for a first install, from a browser. The releases are private: the cloud lists
 * the installers and redirects each download to a short-lived signed URL.
 */

export type DesktopOs = 'macos' | 'windows' | 'linux';

export const INSTALLER_LABELS: Record<DesktopInstallerTarget, { os: string; hint: string }> = {
  'macos-arm64': { os: 'macOS', hint: 'Apple Silicon' },
  'macos-x64': { os: 'macOS', hint: 'Intel' },
  'windows-x64': { os: 'Windows', hint: '64-bit installer' },
  'linux-appimage': { os: 'Linux', hint: 'AppImage' },
  'linux-deb': { os: 'Linux', hint: 'Debian, Ubuntu (.deb)' },
};

/**
 * The computer's operating system, from the browser; null on a phone or tablet, which runs no
 * Desktop. The processor is not told reliably: macOS offers Apple Silicon first.
 */
export function detectDesktopOs(userAgent: string): DesktopOs | null {
  if (/android|iphone|ipad|ipod|mobile/i.test(userAgent)) return null;
  if (/windows/i.test(userAgent)) return 'windows';
  if (/mac os x|macintosh/i.test(userAgent)) return 'macos';
  if (/linux|x11|cros/i.test(userAgent)) return 'linux';
  return null;
}

export function browserDesktopOs(): DesktopOs | null {
  return typeof navigator === 'undefined' ? null : detectDesktopOs(navigator.userAgent);
}

function installerOs(target: DesktopInstallerTarget): DesktopOs {
  return target.startsWith('macos') ? 'macos' : target.startsWith('windows') ? 'windows' : 'linux';
}

/** The installer to offer first: this computer's (the first published for its system). */
export function preferredInstaller(
  release: DesktopDownloadChannel,
  os: DesktopOs | null,
): DesktopDownloadChannel['installers'][number] | null {
  return release.installers.find((installer) => installerOs(installer.target) === os) ?? null;
}

export function desktopDownloadHref(
  target: DesktopInstallerTarget,
  channel: DesktopUpdateChannel,
): string {
  return `/api/desktop/download?target=${target}&channel=${channel}`;
}

export function formatInstallerSize(bytes: number): string {
  const megabytes = bytes / 1024 / 1024;
  return `${megabytes.toFixed(megabytes >= 100 ? 0 : 1)} MB`;
}

export async function loadDesktopDownloads(): Promise<DesktopDownloads> {
  return desktopDownloadsSchema.parse(
    await apiFetch('/api/desktop/download', { cache: 'no-store' }),
  );
}
